import { CommentProps, PaperProps, StateBoxProps } from "../HSM.tsx";
import { ObjectId } from "bson";

export function getIndexOfState(
    parentProps: StateBoxProps,
    id: number,
) : number {
  if(!parentProps.children) {
    // children property is undefined. we treat this the same as
    // having no children
    return -1;
  }

  for(let i = 0; i < parentProps.children.length; i++) {
    if(parentProps.children[i].id === id) {
      return i;
    }
  }

  return -1;
}

export function getIndexOfStateUid(
  parentProps: StateBoxProps,
  uid: number,
) : number {
if(!parentProps.children) {
  // children property is undefined. we treat this the same as
  // having no children
  return -1;
}

for(let i = 0; i < parentProps.children.length; i++) {
  if(parentProps.children[i].uid === uid) {
    return i;
  }
}

return -1;
}
  
  export function getPropsOfState(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    id: number,
  ) : StateBoxProps {
    if(!parentProps.children) {
      // children property is undefined. we treat this the same as
      // having no children
      return undefined;
    }
  
    if(relCrumbIds.length === 1) {
      return parentProps.children[getIndexOfState(parentProps, id)];
    }
  
    let indexOfNextChild = getIndexOfState(parentProps, relCrumbIds[1]);
  
    if(indexOfNextChild === -1) {
      // child with the next ID has not been found
      return undefined;
    }
  
    return getPropsOfState(parentProps.children[indexOfNextChild], relCrumbIds.slice(1,undefined), id);
  }
  
  /**
   * Recursively retrieves an array of all states the user would see given the input crumb trail.
   * @param parentProps The current parent's props
   * @param relCrumbIds Relative crumb IDs. relCrumbIds[0] should always be parentProps. This defines
   * the path we take down the HSMs.
   * @returns An array containing the states the user would see on the current layer
   */
  export function getStatesCurrentLayer(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
  ) : StateBoxProps[] {
    if(!parentProps.children) {
      // children property is undefined. we treat this the same as
      // having no children
      return [];
    }
  
    if(relCrumbIds.length === 1) {
      return parentProps.children;
    }
  
    let indexOfNextChild = getIndexOfState(parentProps, relCrumbIds[1]);
  
    if(indexOfNextChild === -1) {
      // child with the next ID has not been found
      return [];
    }
  
    return getStatesCurrentLayer(parentProps.children[indexOfNextChild], relCrumbIds.slice(1,undefined));
  }

  export function updateTitleOfActivity(
    activityProps: StateBoxProps,
    value: string
  ) : StateBoxProps {
    return {...activityProps, text:value};
  }
  
  export function updatePropertyOfState(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    id: number,
    property: string,
    value: any,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      return parentProps.children.map((v) => {
        if(v.id === id) {
          v[property] = value;
          v['uid'] = new ObjectId().toHexString();
        }
        return v;
      });
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: updatePropertyOfState(v,relCrumbIds.slice(1),
                                            id, property, value)}
      ) : v;
    });
  }
  
  /**
   * Recursively adds a state to the HSM, and returns the resulting children of the supplied parent after
   * the add operation.
   * @param parentProps The active parent state's props.
   * @param relCrumbIds Relative crumb IDs. relCrumbIds[0] should always be parentProps. This defines
   * the path we take down the HSMs.
   * @param predId The predecessor ID. The new state will be added directly after the state with this ID.
   * @param targetProps The props of the new state to add
   * @returns 
   */
  export function addState(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    predId: number,
    targetProps: StateBoxProps,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      // the predecessor state lives in the current layer. 
      if(predId === -1) {
        // client is trying to add state to start of children array
        return [targetProps, ...parentProps.children];
      }
  
      // we return the children of the current parent, except with the target props added right after
      // the props of the predecessor state
      let predStateIndex = getIndexOfState(parentProps, predId);
      let beforeNewState = parentProps.children.slice(0, predStateIndex+1);
      let afterNewState = parentProps.children.slice(predStateIndex+1, parentProps.children.length);
  
      return [...beforeNewState, targetProps, ...afterNewState];
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: addState(v,relCrumbIds.slice(1), predId, targetProps)}
      ) : v;
    });
  }

  export function addStateUid(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    predUid: number,
    targetProps: StateBoxProps,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      // the predecessor state lives in the current layer. 
      if(predUid === -1) {
        // client is trying to add state to start of children array
        return [targetProps, ...parentProps.children];
      }
  
      // we return the children of the current parent, except with the target props added right after
      // the props of the predecessor state
      let predStateIndex = getIndexOfStateUid(parentProps, predUid);
      let beforeNewState = parentProps.children.slice(0, predStateIndex+1);
      let afterNewState = parentProps.children.slice(predStateIndex+1, parentProps.children.length);
  
      return [...beforeNewState, targetProps, ...afterNewState];
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: addStateUid(v,relCrumbIds.slice(1), predUid, targetProps)}
      ) : v;
    });
  }
  
  export function deleteState(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    id: number,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      return parentProps.children.filter((v) => v.id !== id);
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: deleteState(v,relCrumbIds.slice(1), id)}
      ) : v;
    }); 
  }

  export function deleteStateUid(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    uid: number,
  ) : StateBoxProps[] {
    console.log('uid', uid);
    console.log(parentProps.children);
    if(relCrumbIds.length === 1) {
      console.log(parentProps.children);
      return parentProps.children.filter((v) => v.uid !== uid);
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: deleteStateUid(v,relCrumbIds.slice(1), uid)}
      ) : v;
    }); 
  }

  /**
   * Converts a list of crumb ids to a list of their respective UIDs.
   * @param parentProps The current parent's props
   * @param relCrumbIds Relative crumb IDs. relCrumbIds[0] should always be parentProps. This defines
   * the path we take down the HSMs.
   * @param accumulator An array that accumulates to construct the answer. Initial calls should
   * pass in [] for this.
   */
  export function crumbsToUids(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    accumulator: string[],
  ) {
      if(relCrumbIds.length === 0) {
        return accumulator;
      }

      if(relCrumbIds.length === 1) {
        return accumulator;
      }

      let nextParent;

      for(let i = 0; i < parentProps.children.length; i++) {
        if(parentProps.children[i]['id'] === relCrumbIds[1]) {
          nextParent = parentProps.children[i];
          accumulator.push(parentProps.children[i]['uid']);
        }
      }

      return crumbsToUids(nextParent, relCrumbIds.slice(1), accumulator);
  }
  
  export function addComment(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    id: number,
    commentProps: CommentProps,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      // the state we are adding a comment to lives in the current layer.
  
      // we return the children of the current parent, except with the comment added to 
      // the right state
  
      return parentProps.children.map((v) => {
        if(v.id != id) {
          return v;
        }
  
        let currentComments = !v.comments ? [] : v.comments;
  
        return {...v, comments:[...currentComments, commentProps]};
      });
    }
  
    let highLevelResult = parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: addComment(v,relCrumbIds.slice(1), id, commentProps)}
      ) : v;
    });
  
    return highLevelResult;
  
  }
  
  export function addPaper(
    parentProps: StateBoxProps,
    relCrumbIds: number[],
    id: number,
    paperProps: PaperProps,
  ) : StateBoxProps[] {
    if(relCrumbIds.length === 1) {
      // the state we are adding a comment to lives in the current layer.
  
      // we return the children of the current parent, except with the comment added to 
      // the right state
  
      return parentProps.children.map((v) => {
        if(v.id != id) {
          return v;
        }
  
        let currentPapers = !v.papers ? [] : v.papers;
  
        return {...v, papers:[...currentPapers, paperProps]};
      });
    }
  
    return parentProps.children.map((v) => {
      return v.id === relCrumbIds[1] ? (
        {...v, children: addPaper(v,relCrumbIds.slice(1), id, paperProps)}
      ) : v;
    });
  }
  
  export function getNewStateProps(
    nextUID,
    layersBelow: number,
    setActivityProps,
    crumbIds,
    setCrumbIds,
    setCursor,
  ) {
    const getNewText = () => {
        if(layersBelow === 3) {
          return 'Composite task';
        } else if(layersBelow === 2) {
          return 'Task';
        } else if(layersBelow === 1) {
          return 'Composite skill';
        } else if(layersBelow === 0) {
          return 'Skill';
        }
    }
  
    const getNewType = () => {
      if(layersBelow === 3) {
        return 'composite task';
      } else if(layersBelow === 2) {
        return 'task';
      } else if(layersBelow === 1) {
        return 'composite skill';
      } else if(layersBelow === 0) {
        return 'skill';
      }
    }
  
    let newStateId = nextUID();
    let newStateUid = new ObjectId().toHexString();
    let onClickFunc = layersBelow === 0 ? () => {} 
    : () => {
      setCrumbIds((prevCrumbIds) => [...prevCrumbIds, newStateId]);
      setCursor('auto');
    }
    let newStateProps = {
      id: newStateId,
      type: getNewType(),
      text: getNewText(),
      uid: newStateUid,
      setText: (txt) => {
        setActivityProps((prevActivity) => {
          return {...prevActivity, children:updatePropertyOfState(prevActivity,
                                                              [...crumbIds],
                                                              newStateId,
                                                              'text', txt)};
        });
      },
      setEditing: (editing) => {
        setActivityProps((prevActivity) => {
          return {...prevActivity, children:updatePropertyOfState(prevActivity,
                                                                  [...crumbIds],
                                                                  newStateId,
                                                                  'editing', editing)};
        });
      },
      deleteSelf: () => {
        setActivityProps((prevActivity) => {
          return {...prevActivity, children:deleteState(prevActivity,
                                                        [...crumbIds],
                                                        newStateId)};
        });
      },
      onClick: onClickFunc,
      setCursorPointer: () => setCursor('pointer'),
      setCursorAuto: () => setCursor('auto'),
  
    }
    if(layersBelow === 0) {
      return {...newStateProps, children:[]};
    }
  
    return {...newStateProps, children: [getNewStateProps(nextUID, layersBelow-1,setActivityProps,crumbIds,setCrumbIds,setCursor)],
    };
  }
  
  export function getStateTypeFromLayers(
    layersBelow: number
  ) {
    switch(layersBelow) {
      case 3:
        return 'composite task';
      case 2:
        return 'task';
      case 1:
        return 'composite skill';
      case 0:
        return 'skill'
    }
  
    return '';
  }